import { UPLOAD_IMAGE_TYPES } from '@avrahami/contracts';

/**
 * SVG is the usual format of a logo, but a script can hide in it, so the API refuses it
 * (adr/0051). The admins take it anyway and turn it into a PNG in the browser before uploading:
 * drawn as an image, nothing inside it runs, and the server only ever receives pixels.
 */
export const SVG_TYPE = 'image/svg+xml';

/** What the file choosers of the admins offer. */
export const IMAGE_UPLOAD_ACCEPT = [...UPLOAD_IMAGE_TYPES, SVG_TYPE, '.svg'].join(',');

/** The longer side of the PNG: sharp in a store's header, and on a printed flyer. */
export const SVG_RASTER_SIDE = 1200;

/** The SVG cannot be read or drawn. */
export class SvgImageError extends Error {}

/** Lengths in CSS pixels; anything else (a percentage, em) says nothing about the size. */
const UNIT_PIXELS: Record<string, number> = {
  '': 1,
  px: 1,
  pt: 96 / 72,
  pc: 16,
  mm: 96 / 25.4,
  cm: 96 / 2.54,
  in: 96,
};

function svgLength(value: string | null): number | undefined {
  const match = /^\s*(\d*\.?\d+)\s*([a-z]*)\s*$/i.exec(value ?? '');
  if (match === null) return undefined;
  const length = Number(match[1]) * (UNIT_PIXELS[(match[2] ?? '').toLowerCase()] ?? 0);
  return length > 0 ? length : undefined;
}

function svgViewBox(value: string | null) {
  const text = (value ?? '').trim();
  const parts = text.split(/[\s,]+/).map(Number);
  const [, , width = 0, height = 0] = parts;
  if (parts.length !== 4 || parts.some((part) => !Number.isFinite(part))) return undefined;
  return width > 0 && height > 0 ? { text, width, height } : undefined;
}

export interface SvgDrawing {
  viewBox: string;
  width: number;
  height: number;
}

/**
 * How to draw an SVG at the PNG's size, from its root's attributes. The view box is what scales
 * it, so one is made from the width and height when it has none; without either, its size is
 * unknown and the answer is null.
 */
export function svgDrawing(root: {
  viewBox: string | null;
  width: string | null;
  height: string | null;
}): SvgDrawing | null {
  const box = svgViewBox(root.viewBox);
  const width = box?.width ?? svgLength(root.width);
  const height = box?.height ?? svgLength(root.height);
  if (width === undefined || height === undefined) return null;
  const scale = SVG_RASTER_SIDE / Math.max(width, height);
  return {
    viewBox: box?.text ?? `0 0 ${String(width)} ${String(height)}`,
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function isSvgFile(file: File): boolean {
  return file.type === SVG_TYPE || /\.svg$/i.test(file.name);
}

/** The SVG with its size written on its root; browsers disagree about one that has none. */
function sizedSvg(text: string): { svg: string; drawing: SvgDrawing } {
  const parsed = new DOMParser().parseFromString(text, SVG_TYPE);
  const root = parsed.documentElement;
  if (root.localName !== 'svg' || parsed.querySelector('parsererror') !== null) {
    throw new SvgImageError('Not an SVG');
  }
  const drawing = svgDrawing({
    viewBox: root.getAttribute('viewBox'),
    width: root.getAttribute('width'),
    height: root.getAttribute('height'),
  });
  if (drawing === null) throw new SvgImageError('An SVG without a size');
  root.setAttribute('viewBox', drawing.viewBox);
  root.setAttribute('width', String(drawing.width));
  root.setAttribute('height', String(drawing.height));
  return { svg: new XMLSerializer().serializeToString(parsed), drawing };
}

async function drawnPng(svg: string, drawing: SvgDrawing): Promise<Blob> {
  const url = URL.createObjectURL(new Blob([svg], { type: SVG_TYPE }));
  try {
    const image = new Image();
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = reject;
      image.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = drawing.width;
    canvas.height = drawing.height;
    const context = canvas.getContext('2d');
    if (context === null) throw new SvgImageError('No canvas to draw on');
    context.drawImage(image, 0, 0, drawing.width, drawing.height);
    const png = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/png');
    });
    if (png === null) throw new SvgImageError('The canvas gave no PNG');
    return png;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * The file to upload: a transparent PNG for an SVG, any other file as it is. Throws
 * SvgImageError when the SVG cannot be turned into a picture.
 */
export async function uploadableImage(file: File): Promise<File> {
  if (!isSvgFile(file)) return file;
  try {
    const { svg, drawing } = sizedSvg(await file.text());
    const png = await drawnPng(svg, drawing);
    return new File([png], file.name.replace(/\.svg$/i, '.png'), { type: 'image/png' });
  } catch (failure) {
    throw failure instanceof SvgImageError ? failure : new SvgImageError(String(failure));
  }
}
